import React, { useEffect, useMemo, useState } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { StoreBodyBlock } from 'constants/enums';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import { z } from 'zod';

import { templateStudioSchema } from '../../validation-schema';

import { TemplateStudioBundleForm } from './BlocksForms/Bundle';
import { TemplateStudioPromotionForm } from './BlocksForms/Promotion';
import { TemplateStudioRichTextForm } from './BlocksForms/RichText';
import { TemplateStudioRollingOffersForm } from './BlocksForms/RollingOffers';
import { DesignBlockMenuItem } from './Components/DesignBlockMenuItem';
import { FormsSeparator } from './Components/FormMenuSeparator';
import { SortableBlockItem } from './Components/SortableBlockItem';

const createMapBlockToFormComponent = (): Partial<
  Record<StoreBodyBlock, React.ReactNode>
> => ({
  [StoreBodyBlock.specialOffers]: <TemplateStudioPromotionForm />,
  [StoreBodyBlock.bundles]: <TemplateStudioBundleForm />,
  [StoreBodyBlock.rollingOffers]: <TemplateStudioRollingOffersForm />,
  [StoreBodyBlock.richText]: <TemplateStudioRichTextForm />
});

export const TemplateStudioDraggableBlockForms: React.FC<{
  defaultBodyBlocksOrder: StoreBodyBlock[];
}> = ({ defaultBodyBlocksOrder }) => {
  const { setOpenSidebarBlock } = useTemplateStudioState();
  const { control, setValue } =
    useFormContext<z.infer<typeof templateStudioSchema>>();
  const currentBodyBlocksOrder = useWatch({ control, name: 'bodyBlocksOrder' });

  const [draggableBlocks, setDraggableBlocks] = useState<StoreBodyBlock[]>(
    defaultBodyBlocksOrder
  );
  const [activeId, setActiveId] = useState<string | null>(null);

  const hiddenBlocks: StoreBodyBlock[] = useMemo(
    () =>
      defaultBodyBlocksOrder.filter(
        (block) => !currentBodyBlocksOrder.includes(block)
      ),
    [currentBodyBlocksOrder, defaultBodyBlocksOrder]
  );

  useEffect(() => {
    const initialOrder =
      currentBodyBlocksOrder.length > 0
        ? currentBodyBlocksOrder
        : defaultBodyBlocksOrder;

    const finalOrder = [...initialOrder, ...hiddenBlocks];

    setDraggableBlocks(finalOrder);
  }, [currentBodyBlocksOrder, hiddenBlocks, defaultBodyBlocksOrder]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setOpenSidebarBlock(undefined);
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setDraggableBlocks((items) => {
        const oldIndex = items.findIndex(
          (item) => String(item) === String(active.id)
        );
        const newIndex = items.findIndex(
          (item) => String(item) === String(over.id)
        );

        const newItems = arrayMove(items, oldIndex, newIndex);

        // Only set visible blocks to the form value
        const visibleBlocks = newItems.filter(
          (block) => !hiddenBlocks.includes(block)
        );
        setValue('bodyBlocksOrder', visibleBlocks, {
          shouldValidate: true,
          shouldDirty: true
        });

        return newItems;
      });
    } else {
      // Even if no reordering happened, update the form value
      const visibleBlocks = draggableBlocks.filter(
        (block) => !hiddenBlocks.includes(block)
      );
      setValue('bodyBlocksOrder', visibleBlocks, {
        shouldValidate: true,
        shouldDirty: true
      });
    }

    setActiveId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const mapBlockToFormComponent = createMapBlockToFormComponent();

  const items = draggableBlocks.map((block) => String(block));
  const activeBlock = activeId
    ? draggableBlocks.find((block) => String(block) === activeId)
    : null;
  const activeFormComponent = activeBlock
    ? mapBlockToFormComponent[activeBlock as StoreBodyBlock]
    : null;
  const activeIsHidden = activeBlock
    ? hiddenBlocks.includes(activeBlock)
    : false;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {draggableBlocks.map((block) => {
          const formComponent =
            mapBlockToFormComponent[block as StoreBodyBlock];
          const isHidden = hiddenBlocks.includes(block);
          const isDragging = activeId === String(block);

          return (
            <SortableBlockItem
              key={block}
              id={String(block)}
              isHidden={isHidden}
            >
              <DesignBlockMenuItem
                blockType={block}
                isHidden={isHidden}
                isDragging={activeId !== null}
                isCurrentItemDragged={isDragging}
              >
                {formComponent}
              </DesignBlockMenuItem>
              <FormsSeparator />
            </SortableBlockItem>
          );
        })}
      </SortableContext>
      <DragOverlay>
        {activeId && activeBlock ? (
          <div className="cursor-grabbing shadow-lg bg-white z-50">
            <DesignBlockMenuItem
              blockType={activeBlock}
              isHidden={activeIsHidden}
              isDragging={true}
              isCurrentItemDragged={true}
            >
              {activeFormComponent}
            </DesignBlockMenuItem>
            <FormsSeparator />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
